/**
 * CategoriesTab - Admin Category Management Component
 * Extracted from AdminPage.jsx for better code organization
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Tag, Plus, X, AlertTriangle } from 'lucide-react';
import { useAdminTranslation } from '../../utils/useAdminTranslation';

const API = process.env.REACT_APP_BACKEND_URL;

export const CategoriesTab = ({ token }) => {
  const { tx } = useAdminTranslation();
  
  // State
  const [customCategories, setCustomCategories] = useState([]);
  const [sensitiveCategories, setSensitiveCategories] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newSensitiveCategoryName, setNewSensitiveCategoryName] = useState('');

  // Fetch categories
  useEffect(() => {
    if (token) {
      fetchCategories();
      fetchSensitiveCategories();
    }
  }, [token]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomCategories(res.data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchSensitiveCategories = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/sensitive-categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSensitiveCategories(res.data.categories || []);
    } catch (error) {
      console.error('Error fetching sensitive categories:', error);
    }
  };

  // Handlers
  const handleAddCategory = () => {
    if (newCategoryName.trim()) {
      const newCat = newCategoryName.trim();
      if (!customCategories.includes(newCat)) {
        setCustomCategories([...customCategories, newCat].sort());
        setNewCategoryName('');
        toast.success(tx(`Categoría "${newCat}" añadida`, `Category "${newCat}" added`));
      } else {
        toast.error(tx('Esta categoría ya existe', 'This category already exists'));
      }
    }
  };

  const handleDeleteCategory = (cat) => {
    if (window.confirm(tx(`¿Eliminar la categoría "${cat}"?`, `Delete category "${cat}"?`))) {
      setCustomCategories(customCategories.filter(c => c !== cat));
      toast.success(tx(`Categoría "${cat}" eliminada`, `Category "${cat}" deleted`));
    }
  };

  const handleAddSensitiveCategory = async () => {
    if (!newSensitiveCategoryName.trim()) return;
    try {
      await axios.post(`${API}/api/admin/sensitive-categories`, 
        { category: newSensitiveCategoryName.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewSensitiveCategoryName('');
      fetchSensitiveCategories();
      toast.success(tx('Categoría sensible añadida', 'Sensitive category added'));
    } catch (error) {
      toast.error(error.response?.data?.detail || tx('Error al añadir categoría', 'Error adding category'));
    }
  };

  const handleDeleteSensitiveCategory = async (cat) => {
    if (!window.confirm(tx(`¿Eliminar "${cat}"?`, `Delete "${cat}"?`))) return;
    try {
      await axios.delete(`${API}/api/admin/sensitive-categories/${encodeURIComponent(cat)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchSensitiveCategories();
      toast.success(tx('Categoría sensible eliminada', 'Sensitive category deleted'));
    } catch (error) {
      toast.error(error.response?.data?.detail || tx('Error al eliminar', 'Error deleting'));
    }
  };

  return (
    <div className="space-y-6">
      {/* Regular Categories Card */}
      <Card>
        <CardHeader>
          <div>
            <CardTitle className="flex items-center gap-2">
              <Tag className="h-5 w-5" />
              {tx('Gestión de Categorías', 'Category Management')}
            </CardTitle>
            <CardDescription>
              {tx('Crea y gestiona las categorías de productos', 'Create and manage product categories')}
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          {/* Add new category */}
          <div className="flex gap-2 mb-6">
            <Input
              placeholder={tx('Nombre de la nueva categoría...', 'New category name...')}
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              className="max-w-sm"
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAddCategory();
              }}
            />
            <Button onClick={handleAddCategory}>
              <Plus className="h-4 w-4 mr-2" />
              {tx('Añadir', 'Add')}
            </Button>
          </div>
          
          {/* Categories list */}
          <div className="flex flex-wrap gap-2">
            {customCategories.map((cat) => (
              <Badge key={cat} variant="secondary" className="flex items-center gap-1 py-2 px-3 text-sm">
                {cat}
                <button
                  type="button"
                  onClick={() => handleDeleteCategory(cat)}
                  className="ml-1 hover:text-destructive"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
          
          <p className="text-sm text-muted-foreground mt-4">
            {tx(`Total: ${customCategories.length} categorías`, `Total: ${customCategories.length} categories`)}
          </p>
        </CardContent>
      </Card>

      {/* Sensitive Categories Card */}
      <Card>
        <CardHeader>
          <div>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              {tx('Categorías Sensibles', 'Sensitive Categories')}
            </CardTitle>
            <CardDescription>
              {tx(
                'Las categorías sensibles se excluyen del filtro normal y requieren precio especial',
                'Sensitive categories are excluded from normal filters and require special pricing'
              )}
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          {/* Add new sensitive category */}
          <div className="flex gap-2 mb-6">
            <Input
              placeholder={tx('Nueva categoría sensible...', 'New sensitive category...')}
              value={newSensitiveCategoryName}
              onChange={(e) => setNewSensitiveCategoryName(e.target.value.toUpperCase())}
              className="max-w-sm"
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAddSensitiveCategory();
              }}
            />
            <Button 
              onClick={handleAddSensitiveCategory}
              variant="outline"
              className="border-amber-500 text-amber-600 hover:bg-amber-50"
            >
              <Plus className="h-4 w-4 mr-2" />
              {tx('Añadir', 'Add')}
            </Button>
          </div>
          
          {/* Sensitive categories list */}
          <div className="flex flex-wrap gap-2">
            {sensitiveCategories.map((cat) => (
              <Badge key={cat} variant="destructive" className="flex items-center gap-1 py-2 px-3 text-sm bg-amber-100 text-amber-800 hover:bg-amber-200">
                {cat}
                <button
                  type="button"
                  onClick={() => handleDeleteSensitiveCategory(cat)}
                  className="ml-1 hover:text-red-600"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
            {sensitiveCategories.length === 0 && (
              <p className="text-muted-foreground text-sm">
                {tx('No hay categorías sensibles definidas', 'No sensitive categories defined')}
              </p>
            )}
          </div>
          
          <p className="text-sm text-muted-foreground mt-4">
            {tx(
              `Total: ${sensitiveCategories.length} categorías sensibles`,
              `Total: ${sensitiveCategories.length} sensitive categories`
            )}
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default CategoriesTab;
